<script setup>
import { useAbility } from '@casl/vue'
import { VDataTableServer } from 'vuetify/lib/labs/components.mjs'
import { cookieRef } from '@/@layouts/stores/config'
import RiskAssessmentService from '@/services/quality/risk-assessment.service'
import RiskService from '@/services/quality/risk.service'
import { RiskAssessmentStatus, riskAssessmentStatusColor, riskAssessmentStatusTitle } from '@/enums/quality/risk-assessment-status'
import AssessmentCreateDrawer from '@/views/quality/risk/assessment/AssessmentCreateDrawer.vue'

// meta.subject/meta.action are CASL keys read by the router guard — NOT a route
// name. The route name derives from this file's path:
// app-quality-risk-assessments.
definePage({
  meta: {
    subject: 'risk.menu',
    action: 'assessments',
  },
})

const router = useRouter()
const { can } = useAbility()

const unitId = cookieRef('selectedUnit', null)

const assessments = ref([])
const totalAssessments = ref(0)
const isLoading = ref(false)
const isCreateDrawerVisible = ref(false)
const riskTypes = ref([])

const page = ref(1)
const itemsPerPage = ref(15)
const search = ref('')
const filters = ref({ risk_type_id: null, status: null })

const headers = [
  { title: 'Title', key: 'title' },
  { title: 'Type', key: 'risk_type.name', sortable: false },
  { title: 'Status', key: 'status' },
  { title: 'Risks', key: 'linked_risk_count', sortable: false },
  { title: 'Performed By', key: 'performer.name', sortable: false },
  { title: 'Completed', key: 'completed_at' },
]

const statusOptions = Object.values(RiskAssessmentStatus).map(value => ({
  title: riskAssessmentStatusTitle(value),
  value,
}))

// Monotonic token: an earlier in-flight response must never clobber a later one
// when filters change quickly. Same pattern as the register.
let fetchToken = 0

const fetchAssessments = async () => {
  const token = ++fetchToken

  isLoading.value = true
  await RiskAssessmentService.list({
    page: page.value,
    per_page: itemsPerPage.value,
    search: search.value || null,
    unit_id: unitId.value,
    ...filters.value,
  }, data => {
    if (token !== fetchToken) return
    assessments.value = data?.data ?? []
    totalAssessments.value = data?.meta?.total ?? 0
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    assessments.value = []
    totalAssessments.value = 0
    isLoading.value = false
  })
}

// 500ms debounce before the query reaches the API, per project convention.
const onSearch = useDebounceFn(() => {
  page.value = 1
  fetchAssessments()
}, 500)

watch(search, onSearch)

// Filter changes reset to page 1 — otherwise a filter applied on page 3 returns
// a stale page. Watchers only: pairing them with @update:options would fire
// every fetch twice.
watch(filters, () => {
  page.value = 1
  fetchAssessments()
}, { deep: true })

watch([page, itemsPerPage, unitId], fetchAssessments)

const fetchTypes = async () => {
  await RiskService.listTypes({ unit_id: unitId.value }, data => {
    riskTypes.value = data?.data ?? data ?? []
  })
}

fetchAssessments()
fetchTypes()

const openAssessment = id => router.push({ name: 'app-quality-risk-assessments-id', params: { id } })

const onCreated = assessment => {
  isCreateDrawerVisible.value = false
  if (assessment?.id) openAssessment(assessment.id)
  else fetchAssessments()
}
</script>

<template>
  <div>
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <VRow>
          <VCol
            cols="12"
            md="4"
          >
            <AppSelect
              v-model="filters.risk_type_id"
              label="Assessment Type"
              :items="riskTypes"
              item-title="name"
              item-value="id"
              clearable
              placeholder="All types"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
          >
            <AppSelect
              v-model="filters.status"
              label="Status"
              :items="statusOptions"
              clearable
              placeholder="All statuses"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center gap-4">
        <AppTextField
          v-model="search"
          placeholder="Search assessments..."
          prepend-inner-icon="tabler-search"
          style="max-inline-size: 20rem;"
        />
        <VSpacer />
        <VBtn
          v-if="can('assess', 'quality.risk')"
          prepend-icon="tabler-plus"
          @click="isCreateDrawerVisible = true"
        >
          New Assessment
        </VBtn>
      </VCardText>

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="assessments"
        :items-length="totalAssessments"
        :loading="isLoading"
        class="cursor-pointer"
        @click:row="(_, { item }) => openAssessment(item.id)"
      >
        <template #item.status="{ item }">
          <VChip
            :color="riskAssessmentStatusColor(item.status)"
            size="small"
            label
          >
            {{ riskAssessmentStatusTitle(item.status) }}
          </VChip>
        </template>

        <template #item.linked_risk_count="{ item }">
          <span
            v-if="item.nil_return"
            class="text-medium-emphasis"
          >Nil return</span>
          <span v-else>{{ item.linked_risk_count ?? 0 }}</span>
        </template>

        <template #item.completed_at="{ item }">
          {{ item.completed_at ? formatDate(item.completed_at) : '—' }}
        </template>

        <template #no-data>
          <div class="text-center py-8 text-medium-emphasis">
            No assessments recorded yet.
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <AssessmentCreateDrawer
      v-model:is-drawer-open="isCreateDrawerVisible"
      :risk-types="riskTypes"
      :unit-id="unitId"
      @created="onCreated"
    />
  </div>
</template>
